/**
 * ConsoleLogsPanel
 * Panel que muestra los logs de consola capturados.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Chip,
  Paper,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Code as LogIcon,
} from "@mui/icons-material";

const LOG_LEVEL_CONFIG = {
  log: { color: "default", icon: <LogIcon fontSize="small" />, label: "Log" },
  error: { color: "error", icon: <ErrorIcon fontSize="small" />, label: "Error" },
  warn: { color: "warning", icon: <WarningIcon fontSize="small" />, label: "Warn" },
  info: { color: "info", icon: <InfoIcon fontSize="small" />, label: "Info" },
};

const LogEntry = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  const config = LOG_LEVEL_CONFIG[log.level] || LOG_LEVEL_CONFIG.log;
  const hasMultipleArgs = log.args.length > 1;
  const hasLongContent = log.args.some((arg) => arg.length > 100 || arg.includes("\n"));

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1,
        bgcolor: log.level === "error" ? "error.50" : log.level === "warn" ? "warning.50" : "grey.50",
        borderLeft: 4,
        borderColor: `${config.color}.main`,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Chip
          size="small"
          icon={config.icon}
          label={config.label}
          color={config.color}
          sx={{ minWidth: 80 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 100, fontFamily: "monospace" }}
        >
          {formatTimestamp(log.timestamp)}
        </Typography>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          {!expanded && (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {log.args.join(" ")}
            </Typography>
          )}
          <Collapse in={expanded}>
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                bgcolor: "background.paper",
                p: 1,
                borderRadius: 1,
                maxHeight: 400,
                overflow: "auto",
              }}
            >
              {log.args.map((arg, index) => (
                <Box key={index} sx={{ mb: index < log.args.length - 1 ? 1 : 0 }}>
                  {arg}
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
        {(hasMultipleArgs || hasLongContent) && (
          <Tooltip title={expanded ? "Colapsar" : "Expandir"}>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};

const ConsoleLogsPanel = ({ logs }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");

  const filteredLogs = useMemo(() => {
    let result = [...logs].reverse(); // Mostrar más recientes primero

    if (levelFilter !== "all") {
      result = result.filter((log) => log.level === levelFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((log) =>
        log.args.some((arg) => arg.toLowerCase().includes(term))
      );
    }

    return result;
  }, [logs, searchTerm, levelFilter]);

  if (logs.length === 0) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          p: 4,
        }}
      >
        <LogIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No hay logs capturados
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Activa el modo debug para comenzar a capturar logs
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ p: 2, bgcolor: "background.paper" }}>
        <TextField
          size="small"
          placeholder="Buscar en logs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="log">Log</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="warn">Warn</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Logs List */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {filteredLogs.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
            No se encontraron logs con los filtros aplicados
          </Typography>
        ) : (
          filteredLogs.map((log) => <LogEntry key={log.id} log={log} />)
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 1, borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Typography variant="caption" color="text.secondary">
          Mostrando {filteredLogs.length} de {logs.length} logs
        </Typography>
      </Box>
    </Box>
  );
};

export default ConsoleLogsPanel;
